// kfe-html-exercise.js — HTML and AudioHTML reading content.
//
// iOS REFERENCE: KFEModules/Main/MediaViewController.m:631 — dispatches
// `[self showHTML]` whenever `plistModel.media` ends with `.html`. When an
// audio file is also present (line 1527 `[self.plistModel.media hasSuffix:@".html"]`
// inside the audio-bar setup) the same screen renders an audio transport
// alongside the WKWebView. We mirror that one-screen-two-modes design.
//
// PORT REFERENCE (sibling, web): ~/Developer/Rhythmic-Mandarin-Web-delivery_V1/
// src/screens/rm-player.js — the `HTML` and `AudioHTML` branches there are
// the line-for-line shape we follow.
//
// Routing (kfe-exercise-host.js): numeric item.type 6 → plain HTML;
// numeric item.type 4 → AudioHTML. Companion audio for AudioHTML lives in
// `item.file2Name` (RE0101D1.html ↔ RE0101D1.m4a etc.).
//
// Architecture rule reminder: screens consume engines, never the other way
// around. Audio is the shared `services.audio` singleton; we pre-set its
// src so the user-gesture click on `<kfe-audio-bar>`'s ▶ button is enough
// to satisfy iOS Safari's autoplay rule.
//
// Image references inside the HTML: relative paths resolve against the
// iframe's src URL. Deployment must drop a corpus HTML file alongside its
// referenced JPEGs/PNGs (`Media/HTML/RefNote22.html` next to
// `Media/HTML/some_image.jpg`). The pane itself does no rewriting.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import { services } from "../platform-services.js";
import { persistCompletion } from "./completion-persist.js";
import { resolveHtml, resolveAudio } from "../data/media-paths.js";
import {
  ITEM_TYPE_HTML,
  ITEM_TYPE_AUDIO_HTML,
} from "../data/content-items.js";
import { loadExternalTitles, resolveItemTitle } from "../data/item-titles.js";

export class KFEHtmlExercise extends KFEComponent {
  static props = ["itemId"];

  static styles = `
    :host { display: block; padding: 1rem; max-width: 56rem; margin: 0 auto; }
    h2 { margin: 0 0 0.5rem; font-size: 1.25rem; }
    .desc { color: var(--kfe-muted); font-size: 0.875rem; margin-bottom: 1rem; }
    kfe-html-pane { display: block; margin-bottom: 1rem; }
    /* Transport: when the item carries companion audio, the kfe-audio-bar
       sits below the reading pane so the learner reads while listening. */
    kfe-audio-bar { display: flex; margin-top: 1rem; border-radius: 8px;
                    overflow: hidden; }
    /* Brief 167: universal in-lesson nav bar below the reading content. */
    kfe-item-nav-bar { display: block; margin-top: 1rem; }
    .error { color: var(--kfe-error, #e57373); font-size: 0.85rem;
             margin: 0.5rem 0; }
    .empty { padding: 2rem; text-align: center; color: var(--kfe-muted); }
    /* Brief 073: a 1px scroll-to-end marker the IntersectionObserver watches.
       Invisible; it only needs to enter the viewport when the learner scrolls
       to the bottom of the (full-height) reading pane. */
    .end-sentinel { display: block; width: 100%; height: 1px; }
  `;

  /** @type {import("../data/content-items.js").ContentItem | null} */ _item = null;
  _loaded = false;

  // ── Completion gating (Brief 073) ────────────────────────────────────────
  // Reading (plain HTML): scroll-to-end AND dwell ≥ threshold.
  // AudioHTML: scroll-to-end AND dwell AND audio `ended`.
  // Replaces Brief 060b's mark-complete-on-view placeholder.
  /** @type {boolean} */ _scrolledToEnd = false;
  /** @type {boolean} */ _dwellSatisfied = false;
  /** @type {boolean} */ _audioEnded = false;
  /** @type {boolean} */ _requiresAudio = false;
  /** @type {boolean} */ _completionPersisted = false;
  // Brief 199: a completion write is in flight.
  /** @type {boolean} */ _completionPending = false;
  /** @type {boolean} */ _dwellStarted = false;
  /** @type {ReturnType<typeof setTimeout> | null} */ _dwellTimer = null;
  /** @type {IntersectionObserver | null} */ _io = null;

  async onConnected() {
    await Promise.all([
      services.items.loadContentItems(),
      loadExternalTitles(),
    ]);
    const onI18n = () => this._scheduleRender();
    document.addEventListener("kfe:i18n", onI18n);
    this.addCleanup(() => document.removeEventListener("kfe:i18n", onI18n));

    const id = Number(/** @type {any} */ (this).itemId);
    const item = Number.isFinite(id) ? services.items.getItem(id) : undefined;
    this._item = item ?? null;
    // AudioHTML requires the companion audio to finish before completing. A
    // malformed type-4 item with no audio file falls back to reading-only
    // gating so it can still complete (the missing-audio note still renders).
    this._requiresAudio =
      !!item && item.type === ITEM_TYPE_AUDIO_HTML && !!item.file2Name;
    this._loaded = true;
    this._scheduleRender();

    // For AudioHTML, preload the companion audio so the kfe-audio-bar's
    // user-gesture click on ▶ is enough — iOS Safari blocks `el.play()`
    // unless src is already set when the gesture fires.
    if (item && item.type === ITEM_TYPE_AUDIO_HTML && item.file2Name) {
      const url = await resolveAudio({ audioFileName: item.file2Name });
      if (url) {
        services.audio.el.src = url;
        services.audio.el.currentTime = 0;
      }
      // Capture the terminal audio event — one playthrough is required.
      const onAudioEnded = () => this._onAudioEnded();
      services.audio.addEventListener("ended", onAudioEnded);
      this.addCleanup(() => services.audio.removeEventListener("ended", onAudioEnded));
    }

    // Tear-down: stop audio, cancel the dwell timer, drop the observer.
    this.addCleanup(() => {
      services.audio.stop();
      if (this._dwellTimer !== null) { clearTimeout(this._dwellTimer); this._dwellTimer = null; }
      this._io?.disconnect();
      this._io = null;
    });
  }

  // KFEComponent rebuilds the shadow root on every render, so the sentinel is
  // a fresh node each time — re-attach the observer after each render (mirrors
  // kfe-html-pane's iframe re-wiring).
  _renderNow() {
    super._renderNow();
    this._wireScrollSentinel();
  }

  /**
   * Observe the bottom-of-content sentinel. kfe-html-pane sizes its iframe to
   * the full document height, so the *page* scrolls (not the iframe) — when the
   * sentinel enters the viewport the learner has reached the end.
   */
  _wireScrollSentinel() {
    this._io?.disconnect();
    this._io = null;
    if (this._scrolledToEnd) return; // already satisfied; nothing to watch
    const sentinel = this.shadowRoot?.getElementById("end-sentinel");
    if (!sentinel) return;
    if (typeof IntersectionObserver !== "function") {
      // No observer support — don't block completion on scroll detection.
      this._onScrolledToEnd();
      return;
    }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) this._onScrolledToEnd();
    });
    io.observe(sentinel);
    this._io = io;
  }

  /** @param {CustomEvent<{wordCount:number}>} e */
  _onContentLoaded(e) {
    const wordCount = Number(e?.detail?.wordCount) || 0;
    this._startDwell(wordCount);
  }

  /**
   * Start the dwell timer once content has loaded. Threshold: per-item
   * `dwellSeconds` override if present, else `max(3s, 0.2s × wordCount)` —
   * a rough reading-time heuristic (≈300 wpm → 0.2s/word), floored at 3s so
   * very short pages still require a beat of attention. Deliberately
   * approximate; the plist override is the escape hatch for course designers.
   * @param {number} wordCount
   */
  _startDwell(wordCount) {
    if (this._dwellStarted) return;
    this._dwellStarted = true;
    const override = Number(/** @type {any} */ (this._item)?.dwellSeconds);
    const seconds = Number.isFinite(override) && override > 0
      ? override
      : Math.max(3, 0.2 * Math.max(0, wordCount));
    this._dwellTimer = setTimeout(() => {
      this._dwellTimer = null;
      this._onDwellElapsed();
    }, seconds * 1000);
  }

  _onScrolledToEnd() {
    this._scrolledToEnd = true;
    this._io?.disconnect();
    this._io = null;
    this._maybeComplete();
  }

  _onDwellElapsed() {
    this._dwellSatisfied = true;
    this._maybeComplete();
  }

  _onAudioEnded() {
    this._audioEnded = true;
    this._maybeComplete();
  }

  /** Fire completion once every required condition is satisfied. One-shot.
  // Brief 199, criterion 3: the latch is set only AFTER the write has resolved,
  // and a failure leaves it clear so a later terminal event can try again. It
  // used to be set before the write was even attempted, so a rejected write was
  // never retried by this view — six of the 2022 walk's nine lost completions.
  // `_completionPending` keeps a second terminal event in the same lifecycle
  // from starting a second write (learner-store shares the in-flight promise
  // too, so neither can double-count — `completedCount` is a counter, Brief 072).
   */
  async _maybeComplete() {
    if (this._completionPersisted || this._completionPending) return;
    if (!this._scrolledToEnd || !this._dwellSatisfied) return;
    if (this._requiresAudio && !this._audioEnded) return;
    const id = Number(/** @type {any} */ (this).itemId);
    if (!Number.isFinite(id)) return;
    this._completionPending = true;
    const ok = await persistCompletion(id);
    this._completionPending = false;
    this._completionPersisted = ok;
  }

  render() {
    if (!this._loaded) return html`<p>${t("loading")}</p>`;
    const item = this._item;
    if (!item) {
      return html`<div class="empty"><p>${t("html.notFound")}</p></div>`;
    }
    const htmlFile = item.file1Name;
    if (!htmlFile || !htmlFile.toLowerCase().endsWith(".html")) {
      return html`<div class="empty"><p>${t("html.notFound")}</p></div>`;
    }

    const title = resolveItemTitle(item);
    const url = resolveHtml(htmlFile);

    const pane = document.createElement("kfe-html-pane");
    /** @type {any} */ (pane).htmlUrl = url;
    // Brief 073: the pane reports its word count once the iframe loads, which
    // seeds the dwell-completion threshold.
    pane.addEventListener("contentloaded", /** @type {EventListener} */ ((e) => this._onContentLoaded(/** @type {any} */ (e))));

    const frag = document.createDocumentFragment();
    if (title) {
      const h = document.createElement("h2");
      h.textContent = title;
      frag.appendChild(h);
    }
    frag.appendChild(pane);

    if (item.type === ITEM_TYPE_AUDIO_HTML && item.file2Name) {
      const bar = document.createElement("kfe-audio-bar");
      frag.appendChild(bar);
    } else if (item.type === ITEM_TYPE_AUDIO_HTML && !item.file2Name) {
      // Defensive: a type-4 item with no audio is malformed. Render the
      // HTML so the learner still sees the reading content and surface a
      // note so the issue isn't silent.
      const note = document.createElement("p");
      note.className = "error";
      note.textContent = t("html.audioMissing");
      frag.appendChild(note);
    }

    // Scroll-to-end marker; the observer in _wireScrollSentinel watches it.
    const sentinel = document.createElement("div");
    sentinel.id = "end-sentinel";
    sentinel.className = "end-sentinel";
    sentinel.setAttribute("aria-hidden", "true");
    frag.appendChild(sentinel);

    // Brief 167: universal in-lesson nav bar (gate-aware prev/next + playcount
    // badge + add-to-playlist). Self-resolves its lesson context from itemId.
    // Add-to-playlist hides itself for media-less HTML readings (no audio/video
    // to enqueue); AudioHTML keeps it via the companion .m4a.
    const navBar = document.createElement("kfe-item-nav-bar");
    /** @type {any} */ (navBar).itemId = Number(/** @type {any} */ (this).itemId);
    frag.appendChild(navBar);

    return frag;
  }

  /** Sanity helper for hosts that want to know without touching internals. */
  static accepts(itemType) {
    return itemType === ITEM_TYPE_HTML || itemType === ITEM_TYPE_AUDIO_HTML;
  }
}
